import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { ChartLoader } from './ChartWrapper';
import { NoData } from './ui/noData';
import { usePdfMode, PdfDataTable } from './pdf/PdfModeContext';

// Same colours as the "Influencers by Platform" pie
const PLATFORMS = [
  { key: 'instagram', name: 'Instagram', color: '#fbbf24' },
  { key: 'youtube', name: 'YouTube', color: '#c084fc' },
  { key: 'facebook', name: 'Facebook', color: '#93c5fd' },
];

interface Props {
  /** API rows: [{ post_date: 'YYYY-MM-DD', platform, post_count }] */
  rows?: any[];
  /** 'all' stacks the platforms; a single platform shows one bar per day */
  platform: string;
  loading?: boolean;
  height?: number;
}

/**
 * Post Activation Timeline — number of posts per day, by each post's own posted date.
 * Replaces the Monthly Activation Chart (influencer tiers per month).
 * Days between the first and last post with no posts are filled with 0 so gaps in the
 * campaign stay visible.
 */
export function PostActivationTimeline({ rows = [], platform, loading, height = 300 }: Props) {
  const pdfMode = usePdfMode();

  const { chartData, activePlatforms, spansYears } = useMemo(() => {
    const byDate: Record<string, any> = {};
    (Array.isArray(rows) ? rows : []).forEach((r: any) => {
      if (!r?.post_date) return;
      const d = (byDate[r.post_date] = byDate[r.post_date] || { date: r.post_date, total: 0 });
      const count = Number(r.post_count) || 0;
      d[r.platform] = (d[r.platform] || 0) + count;
      d.total += count;
    });

    const dates = Object.keys(byDate).sort();
    if (!dates.length) return { chartData: [] as any[], activePlatforms: [] as typeof PLATFORMS, spansYears: false };

    const first = dayjs(dates[0]);
    const last = dayjs(dates[dates.length - 1]);
    const spansYears = first.year() !== last.year();
    const filled: any[] = [];
    for (let d = first; !d.isAfter(last, 'day'); d = d.add(1, 'day')) {
      const key = d.format('YYYY-MM-DD');
      filled.push({
        ...(byDate[key] || { date: key, total: 0 }),
        label: d.format(spansYears ? "DD MMM 'YY" : 'DD MMM'),
      });
    }

    const active = PLATFORMS.filter(
      (p) => (platform === 'all' || platform === p.key) && filled.some((row) => Number(row[p.key]) > 0)
    );
    return { chartData: filled, activePlatforms: active, spansYears };
  }, [rows, platform]);

  // OLD: one-line "17 Sep" labels, all forced on (tilted past 8 days) - overlapped in the
  // narrower PDF card.
  // const showAllTicks = chartData.length <= 14;
  // const tiltTicks = chartData.length > 8;
  // Two-line labels (day / month) are ~half as wide, so every date fits up to 16 days even
  // in the PDF; longer ranges let recharts drop labels that would collide.
  const showAllTicks = chartData.length <= 16;

  const DateTick = ({ x, y, payload }: any) => {
    const d = dayjs(payload?.value);
    if (!d.isValid()) return null;
    return (
      <g transform={`translate(${x},${y})`}>
        <text textAnchor="middle" fill="#45556c" fontSize={12}>
          <tspan x={0} dy={12}>{d.format('DD')}</tspan>
          <tspan x={0} dy={14} fill="#94a3b8" fontSize={11}>{d.format(spansYears ? "MMM 'YY" : 'MMM')}</tspan>
        </text>
      </g>
    );
  };

  const TimelineTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    return (
      <div className="bg-white px-3 py-3 rounded-lg shadow-md border border-gray-300 flex flex-col" style={{ minWidth: 180 }}>
        <p className="text-slate-800 font-medium fs-14 pb-2 mb-0">{dayjs(row.date).format('DD MMM YYYY')}</p>
        {activePlatforms.length > 1 &&
          activePlatforms.map((p) =>
            row[p.key] ? (
              <span key={p.key} className="d-flex align-items-center" style={{ fontSize: 13, color: '#2b2b2b' }}>
                <span style={{ width: 12, height: 12, backgroundColor: p.color, marginRight: 6 }} />
                {p.name}: {row[p.key]}
              </span>
            ) : null
          )}
        <span style={{ fontSize: 13, color: '#2b2b2b', marginTop: activePlatforms.length > 1 ? 4 : 0 }}>
          {activePlatforms.length > 1 ? 'Total posts' : 'Posts'}: <strong>{row.total}</strong>
        </span>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Post Activation Timeline</CardTitle>
        <CardDescription>Posts published per day</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <ChartLoader type="bar" height={height} />
        ) : chartData.length === 0 ? (
          <NoData message="No posts published in the selected range." />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={height}>
              {/* OLD: no axis titles, and minTickGap dropped every other date label
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} minTickGap={12} />
                <YAxis tick={{ fontSize: 13 }} tickLine={false} axisLine={false} allowDecimals={false} /> */}
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                {/* OLD: dataKey="label" tick={{ fontSize: 12 }} angle={tiltTicks ? -40 : 0}
                    textAnchor={tiltTicks ? 'end' : 'middle'} height={tiltTicks ? 75 : 50} */}
                <XAxis
                  dataKey="date"
                  tick={<DateTick />}
                  tickLine={false}
                  axisLine={false}
                  interval={showAllTicks ? 0 : 'preserveStartEnd'}
                  minTickGap={showAllTicks ? 0 : 8}
                  // room for the two-line tick labels + the "Posted Date" title under them
                  height={58}
                  label={{ value: 'Posted Date', position: 'insideBottom', offset: 0, style: { fontSize: 13, fill: '#45556c' } }}
                />
                <YAxis
                  tick={{ fontSize: 13 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  label={{ value: 'No. of Posts', angle: -90, position: 'insideLeft', style: { fontSize: 13, fill: '#45556c', textAnchor: 'middle' } }}
                />
                <Tooltip content={<TimelineTooltip />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
                {activePlatforms.map((p) => (
                  <Bar
                    key={p.key}
                    isAnimationActive={!pdfMode}
                    dataKey={p.key}
                    stackId="posts"
                    fill={p.color}
                    maxBarSize={28}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            {activePlatforms.length > 1 && (
              <div className="flex flex-wrap justify-center gap-3" style={{ fontSize: 13, color: '#45556c' }}>
                {activePlatforms.map((p) => (
                  <span key={p.key} className="d-flex align-items-center">
                    <span style={{ width: 10, height: 10, backgroundColor: p.color, marginRight: 6, borderRadius: 2 }} />
                    {p.name}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
        {pdfMode && (
          <PdfDataTable
            columns={[
              { label: 'Date' },
              ...(activePlatforms.length > 1 ? activePlatforms.map((p) => ({ label: p.name })) : []),
              { label: 'Posts' },
            ]}
            rows={chartData
              .filter((row) => row.total > 0)
              .map((row) => [
                dayjs(row.date).format('DD MMM YYYY'),
                ...(activePlatforms.length > 1 ? activePlatforms.map((p) => row[p.key] || '-') : []),
                row.total,
              ])}
          />
        )}
      </CardContent>
    </Card>
  );
}
